<script setup lang="ts">
import TheBottomSheet from '@/components/TheBottomSheet.vue'
import CrossIcon from '@/components/icons/CrossIcon.vue'
import { computed } from 'vue'
import { DateTime } from 'luxon'
import type { SpecialCollectibleWinnerNotification } from '@/types/shared/player'
import { useAuth } from '@/composables/auth'
import { useSpecialCollectibleChallenge } from '@/composables/game/challenges'
import ChallengeBoardCard2 from '@/components/ChallengeBoardCard2.vue'
import { availableBadges } from '@/composables/game/badge'
import ClockIcon from '@/assets/images/gold-clock.png'
import CollectibleChallengeIcon from '@/assets/images/challenges/collectible/collectible-challenge-icon.webp'
import HolidayOrbChallengeIcon from '@/assets/images/special-currencies/dropeeorb.webp'
import { CHALLENGE_PRIZE_TYPE } from '@/types/shared/player-config'
import { specialCurrenciesConfig } from '@/config/special-currencies'
import { SPECIAL_CURRENCY_ID } from '@/types/shared/player'
import { formatNumberSemiTruncate } from '@/helpers/utils'


const props = defineProps<{
  show: boolean,
  notification: SpecialCollectibleWinnerNotification
}>()

defineEmits(['hide'])

const { profile } = useAuth()
const { getPrizeTypeImage, getCardMedal } = useSpecialCollectibleChallenge()

const formattedDate = computed(() => {
  return DateTime.fromSeconds(props.notification.date).toFormat('ccc dd LLLL yyyy')
})

const isHolidayOrb = computed(() => props.notification.currency === SPECIAL_CURRENCY_ID.HOLIDAY_ORB)
const challengeIcon = computed(() => isHolidayOrb.value ? HolidayOrbChallengeIcon : CollectibleChallengeIcon)
const defaultChallengeName = computed(() => isHolidayOrb.value ? 'Holiday Hunt' : 'Dropkey Hunt')
</script>

<template>
  <TheBottomSheet
    :show="show"
    centered
    base-modal-class="green-shadow overflow-visible rounded-[32px] mt-10"
    :content-class="`overflow-visible px-0 pt-6 flex flex-col`"
    :outer-content-class="`overflow-visible`"
    border-container-class="overflow-visible"
    @hide="$emit('hide')"
  >
    <template #closeButton>
      <div
        class="absolute w-6 h-6 text-black top-0 right-0 z-3 bg-blue-solid rounded-full border-2 border-blue-lightgray flex items-center justify-center"
        @click="$emit('hide')"
      >
        <CrossIcon class="text-black [&>path]:stroke-[4px] size-[12px]" />
      </div>
    </template>
    <template #baseContent>
      <div class="h-0 w-full relative">
        <div
          class="absolute size-[175px] -top-5 left-1/2 -translate-x-1/2 -translate-y-[60px] scale-110 rounded-full blur-[50px] opacity-90 z-[101]"
          :class="'bg-gradient-to-br from-[#B8852E] via-[#B8852E] to-black'"
        />
        <img
          :src="challengeIcon"
          class="size-[175px] absolute -top-5 left-1/2 -translate-x-1/2 -translate-y-[60px] z-[101] transition-all duration-300"
        >
      </div>
    </template>
    <div class="flex flex-col items-center relative overflow-hidden pt-[80px]">
      <div class="px-6 text-center text-[36px] leading-10 font-bold gold-text mb-8 -tracking-[2px]">
        Yay! You Won!
      </div>
      <div class="-mx-6 h-7 w-[calc(100%+48px)] bg-red-green-orange-countdown-banner flex items-center justify-center">
        <div class="uppercase text-white text-base font-bold text-shadow-default pr-1.5">
          Ended
        </div>
        <img
          class="size-[18px] object-cover"
          :src="ClockIcon"
          alt="Clock"
        >
        <div class="ml-1 golden-text-black-stroke text-base font-bold gold-text-shadow px-0.5">
          {{ formattedDate }}
        </div>
      </div>
      <div class="px-6 mt-6 text-center text-light-white text-base leading-5 -tracking-[0.32px] font-medium">
        Congrats, you're a {{ notification.challengeName ?? defaultChallengeName }} winner!<br>
        The following reward has been sent to<br>
        your account:
      </div>

      <div class="px-6 mt-6 w-full">
        <ChallengeBoardCard2
          :player="profile"
          :index="(notification.position).toString()"
          :medal="getCardMedal(notification.position)"
          :prize-value="'amount' in notification ? notification.amount : 0"
          :prize-name="'nft' in notification ? notification.nft : ''"
          :prize-image="'lottieUrl' in notification ? notification.lottieUrl : getPrizeTypeImage(notification.prizeType, notification.specialCurrencyId)"
          :score-image="specialCurrenciesConfig[notification.currency]?.image"
          :score-value="formatNumberSemiTruncate(notification.score)"
          :is-prize-pool-nft="notification.prizeType === CHALLENGE_PRIZE_TYPE.NFT"
          :is-prize-pool-badge="notification.prizeType === CHALLENGE_PRIZE_TYPE.BADGE"
          :badge="availableBadges.find((badge) => badge.id === notification.badgeId)"
          class="card"
        />
      </div>
    </div>
  </TheBottomSheet>
</template>

<style scoped lang="scss">
@import "@/assets/styles/challenges";

.gold-text {
  background: linear-gradient(90deg, #FFF246 0%, #E5B80B 37%, #FFF571 68.5%, #E5B80B 100%);
  background-clip: text;
  -webkit-background-clip: text;
  -webkit-text-fill-color: transparent;
}

.yellow-countdown-banner {
  background: linear-gradient(90deg, #FFD700 0%, #FFA500 100%);
}

.text-shadow-default {
  text-shadow: 0px 0px 115.874px rgba(0, 0, 0, 0.50);
}

.bg-red-green-orange-countdown-banner {
  background: linear-gradient(90deg, #3DB3C5 0%, #CB6FC4 67.31%, #60433B 100%);
  box-shadow: 0px 8px 12px rgba(0, 0, 0, 0.52);
}
</style>